import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useInvitation } from '../contexts/InvitationContext';
import { useReferral } from '../contexts/ReferralContext';
import { useResponsive } from '../hooks/useResponsive';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Mail, Check, ArrowLeft } from 'react-feather';
import ReferralBanner from '../components/ReferralBanner';
import { TextInput } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { authPath, forgetCoachSetup, isCoachEntry, rememberCoachSetup } from '../utils/coachEntry';
import AudienceSwitch from '../components/AudienceSwitch';

const LoginPage: React.FC = () => {
  const { login, emailLogin, isLoading, error, isAuthenticated } = useAuth();
  const { invitationToken, invitationInfo, clearInvitation } = useInvitation();
  const toast = useToast();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [searchParams] = useSearchParams();
  const { loginWithRedirect } = useAuth0();
  /*
   * [11] A college coach who follows the one link they are given — /coach/signup
   * — is not signed in, so the auth gate sends them here, and here used to be
   * addressed entirely to an athlete's family: "Your Path to College Athletics",
   * "Smart college matching algorithm", "Direct coach communication" — the one
   * promise the coach product deliberately does not make. Nothing on the page
   * mentioned coaches at all.
   *
   * The destination is known (the gate stashes it), so the doorway says where
   * they are going. The family-facing page is untouched for everyone else.
   */
  const coachEntry = isCoachEntry(searchParams);
  // Coach mode sends sign-in on to coach setup. Family mode never does.
  useEffect(() => {
    if (coachEntry) rememberCoachSetup();
    else forgetCoachSetup();
  }, [coachEntry]);
  const [autoRedirecting, setAutoRedirecting] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  // Collapsed-email layout state. Default: three "Continue with X" buttons
  // up top. Clicking "Continue with Email" expands the inline form.
  // Pre-filled from invitation when present so invitees don't retype.
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formEmail, setFormEmail] = useState(invitationInfo?.email || '');
  const [formPassword, setFormPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formProvider, setFormProvider] = useState<{ provider: string; label: string } | null>(null);
  // Set when the inline email/password submit fails with EMAIL_NOT_VERIFIED,
  // so we can surface a "Resend verification email" action next to the error.
  const [formEmailUnverified, setFormEmailUnverified] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  // Proactive provider hint (plan 0.2): on email blur, ask the check-email endpoint
  // (dedicated per-IP limiter, enumeration-safe, never touches the login budget) how
  // this email usually signs in and — for a SOCIAL provider — surface the existing
  // formProvider banner BEFORE the user fails a password attempt. Cached per email;
  // fail-silent, so the post-submit reactive banner stays the safety net.
  const emailHintCacheRef = useRef<Record<string, { provider: string; label: string } | null>>({});
  const handleEmailBlur = async () => {
    const email = formEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) return;
    const cached = emailHintCacheRef.current[email];
    if (cached !== undefined) { if (cached) setFormProvider(cached); return; }
    try {
      const resp = await fetch(`${BACKEND_CONFIG.baseURL}/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      const isSocial = data?.primaryProvider === 'google-oauth2' || data?.primaryProvider === 'apple';
      const hint = isSocial && data.primaryProviderLabel
        ? { provider: data.primaryProvider as string, label: data.primaryProviderLabel as string }
        : null;
      emailHintCacheRef.current[email] = hint;
      if (hint) setFormProvider(hint);
    } catch {
      /* fail silent — the reactive banner still handles the wrong-provider case */
    }
  };

  useEffect(() => {
    if (invitationInfo?.email && !formEmail) {
      setFormEmail(invitationInfo.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationInfo?.email]);

  const handleSocial = (connection: 'google-oauth2' | 'apple') => {
    loginWithRedirect({ authorizationParams: { connection, prompt: 'login' } });
  };

  const handleEmailFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormProvider(null);
    setFormEmailUnverified(false);
    if (!formEmail.trim() || !formPassword) {
      setFormError('Enter your email and password to continue.');
      return;
    }
    setFormSubmitting(true);
    try {
      // Pass invite_token if present — backend auto-accepts the
      // invitation in the same request as the login, killing the
      // post-login bounce through InvitationWelcome. Result carries the
      // accept outcome so we can route to dashboard (guardian / already-
      // member) vs quickStart (athlete).
      const loginResult = await emailLogin(
        formEmail.trim().toLowerCase(),
        formPassword,
        { inviteToken: invitationToken },
      );
      if (loginResult.invited) {
        // already_member: existing user re-clicked their own invite; we
        // already silent-deduped server-side, route to dashboard and
        // clear the invitation (no quickstart needed).
        // Otherwise (fresh invite-accept): route to quickStart so they
        // confirm role + pick/add their athlete via the combined screen.
        // Guardians DO need quickstart too — the auto-accept only
        // links them to the family, not to a specific athlete. Don't
        // clear invitation here: quickStart's invited-combined branch
        // reads invitationInfo from context. clearInvitation fires from
        // the quickstart submit handler instead.
        if (loginResult.alreadyMember) {
          clearInvitation();
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/onboarding/quickStart', { replace: true });
        }
        return;
      }
      // setSessionDirectly inside emailLogin flips isAuthenticated → true,
      // the Navigate guard at the top of LoginPage redirects to /dashboard
      // automatically. Nothing else to do here.
    } catch (err: any) {
      if (err?.code === 'EMAIL_EXISTS_DIFFERENT_PROVIDER' && err?.primaryProvider) {
        setFormProvider({ provider: err.primaryProvider, label: err.primaryProviderLabel || 'another method' });
        setFormError(null);
      } else if (err?.code === 'EMAIL_NOT_VERIFIED') {
        setFormError('Please verify your email first. Check your inbox for the link.');
        setFormEmailUnverified(true);
      } else {
        setFormError(err?.message || 'Invalid email or password.');
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleSocialSwitch = () => {
    if (!formProvider) return;
    if (formProvider.provider === 'google-oauth2') {
      handleSocial('google-oauth2');
    } else if (formProvider.provider === 'apple') {
      handleSocial('apple');
    }
  };

  // Direct password-reset trigger — hits our backend which calls Auth0's
  // /dbconnections/change_password endpoint. No Universal Login bounce.
  // Backend always returns success to avoid leaking which emails exist;
  // we just show the "check your inbox" confirmation either way. Hidden
  // when the form is in EMAIL_EXISTS_DIFFERENT_PROVIDER state (social
  // users have no password to reset).
  const handleResetPassword = async () => {
    setFormError(null);
    if (!formEmail.trim()) {
      setFormError('Enter your email above first.');
      return;
    }
    setResetSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formEmail.trim().toLowerCase() }),
      });
      // Only claim success when the request actually succeeded. Faking a
      // "sent" state on a network failure hides the error AND blocks retry
      // (the reset button is hidden once resetSent flips). The backend's
      // 200 is already anti-enumeration-safe, so an ok response is a real
      // "we tried to send it" — a non-ok / thrown request is not.
      if (!response.ok) {
        throw new Error(`forgot-password ${response.status}`);
      }
      setResetSent(true);
    } catch (err) {
      setFormError("We couldn't send the reset email. Check your connection and try again.");
    } finally {
      setResetSubmitting(false);
    }
  };

  // Parse email verification error format: EMAIL_NOT_VERIFIED:email:message
  const isEmailNotVerified = error?.startsWith('EMAIL_NOT_VERIFIED:');
  const parsedError = isEmailNotVerified && error ? error.split(':') : [];
  const unverifiedEmail = parsedError[1] || '';

  // Parse provider-mismatch error format: EMAIL_EXISTS_DIFFERENT_PROVIDER:provider:label:email:message
  const isWrongProvider = error?.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER:');
  const wrongProviderParts = isWrongProvider && error ? error.split(':') : [];
  const wrongProviderCode = wrongProviderParts[1] || '';
  const wrongProviderLabel = wrongProviderParts[2] || 'another method';
  const wrongProviderEmail = wrongProviderParts[3] || '';
  const wrongProviderMessage = wrongProviderParts.length > 4 ? wrongProviderParts.slice(4).join(':') : '';

  // Map backend provider codes to Auth0 connection names used in loginWithRedirect.
  const PROVIDER_TO_CONNECTION: Record<string, string> = {
    'auth0': 'Username-Password-Authentication',
    'google-oauth2': 'google-oauth2',
    'apple': 'apple',
  };

  // Send the user directly to the correct Auth0 connection, pre-filling their email.
  // prompt:'login' bypasses any lingering SSO session from the failed attempt, so the
  // user actually sees the login form instead of being silently re-authenticated into
  // the same failing provider and bounced back here.
  const handleSignInWithCorrectMethod = () => {
    const connection = PROVIDER_TO_CONNECTION[wrongProviderCode];
    if (!connection) return;
    loginWithRedirect({
      authorizationParams: {
        connection,
        login_hint: wrongProviderEmail || undefined,
        prompt: 'login',
      },
    });
  };

  // When the wrong-provider banner is up, the generic Sign In button needs prompt:'login'
  // too — otherwise Auth0's SSO cookie silently re-auths the user with the same failing
  // provider, trapping them on this page.
  const handleSignIn = () => {
    if (isWrongProvider) {
      loginWithRedirect({ authorizationParams: { prompt: 'login' } });
      return;
    }
    login();
  };

  const displayError = isEmailNotVerified && parsedError.length > 2
    ? parsedError.slice(2).join(':')
    : isWrongProvider ? wrongProviderMessage : error;

  // Handle resend verification email. Accepts an optional email override so
  // the inline email/password form (which knows the typed formEmail, not the
  // top-level unverifiedEmail parsed from the auth error) can reuse it.
  const handleResendVerification = async (emailOverride?: string) => {
    const targetEmail = emailOverride || unverifiedEmail;
    if (!targetEmail) return;
    setResendingEmail(true);
    setResendSuccess(false);
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail }),
      });
      if (response.ok) {
        setResendSuccess(true);
        toast.success('Verification email sent. Check your inbox and spam folder.');
      } else {
        // Previously silent on non-OK — the user tapped "Resend" and got no
        // feedback at all. Surface it.
        toast.error("We couldn't resend the verification email. Please try again.");
      }
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      toast.error("We couldn't resend the verification email. Please try again.");
    }
    setResendingEmail(false);
  };

  // Check for auto-redirect parameter
  useEffect(() => {
    const autoRedirect = searchParams.get('auto');
    if (autoRedirect === 'true' && !isAuthenticated && !autoRedirecting && !isLoading) {
      setAutoRedirecting(true);
      // Small delay to show the loader
      setTimeout(() => {
        login();
      }, 500);
    }
  }, [searchParams, isAuthenticated, login, autoRedirecting, isLoading]);

  // Show loading spinner while checking authentication status
  // This prevents the login page from flashing before redirect
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f0b24',
        fontFamily: 'Manrope, sans-serif',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(249, 85, 13, 0.2)',
          borderTop: '3px solid #f9550d',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    // If there's an active invitation, redirect to invitation page instead of dashboard
    if (invitationToken && invitationInfo) {
      return <Navigate to="/invitation" replace />;
    }
    // Honor any deep-link the user was bounced here from (e.g. email nudge → /onboarding/connectSocials)
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem('postLoginRedirect');
      if (saved) sessionStorage.removeItem('postLoginRedirect');
    } catch {}
    return <Navigate to={saved || '/dashboard'} replace />;
  }

  // Show loader if auto-redirecting
  if (autoRedirecting) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f0b24',
        fontFamily: 'Manrope, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated mesh gradient background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
          `,
          opacity: 0.6
        }} />
        
        {/* Animated orbs */}
        <div style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(249, 85, 13, 0.2) 0%, transparent 60%)',
          top: '-150px',
          right: '-100px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)',
          bottom: '-100px',
          left: '-50px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 10s ease-in-out infinite reverse'
        }} />

        {/* Loading Spinner */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(249, 85, 13, 0.2)',
            borderTop: '4px solid #f9550d',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Redirecting to sign in...
          </p>
        </div>

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes float {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(30px, -30px) scale(1.05); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#0f0b24',
      fontFamily: 'Manrope, sans-serif',
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '24px 16px' : '40px 20px'
    }}>
      {/* Animated mesh gradient background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
        `,
        opacity: 0.6
      }} />
      
      {/* Animated orbs */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(249, 85, 13, 0.2) 0%, transparent 60%)',
        top: '-150px',
        right: '-100px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)',
        bottom: '-100px',
        left: '-50px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 10s ease-in-out infinite reverse'
      }} />
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 60%)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 12s ease-in-out infinite'
      }} />

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) scale(1);
            }
            50% {
              transform: translateY(-30px) scale(1.05);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .login-card {
            animation: fadeInUp 0.6s ease both;
          }

          .logo-pulse {
            animation: pulse 2s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(249, 85, 13, 0.4);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(249, 85, 13, 0);
            }
          }
        `}
      </style>

      {/* Centered Logo Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '40px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div className="logo-pulse" style={{
            width: '80px',
            height: '80px',
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'white',
            padding: '8px',
            flexShrink: 0
          }}>
            <img 
              src="/images/logo-180.png" 
              alt="APS Recruits Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '40px',
              fontWeight: '800',
              letterSpacing: '-0.5px',
              color: 'white'
            }}>
              APS Recruits
            </h1>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: '500'
            }}>
              {coachEntry ? 'College Coach Portal' : 'College Recruiting Platform'}
            </p>
          </div>
        </div>
      </div>

      {/* Two Column Content */}
      <div className="two-column-container" style={{
        display: 'flex',
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
        gap: '60px',
        alignItems: 'flex-start',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
        flex: 1
      }}>
        {/* Left side - Branding */}
        <div className="branding-section" style={{
          flex: 1,
          maxWidth: '500px',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          {/* Tagline */}
          <h2 style={{
            fontSize: '48px',
            fontWeight: '700',
            marginBottom: '32px',
            lineHeight: '1.2',
            margin: '0 0 32px 0'
          }}>
            {coachEntry ? (
              <>
                Recruit from<br/>
                <span style={{ color: '#f9550d' }}>your own bar</span>
              </>
            ) : (
              <>
                Your Path to<br/>
                <span style={{ color: '#f9550d' }}>College Athletics</span>
              </>
            )}
          </h2>

          {/* Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {(coachEntry
              ? [
                  { text: 'Every athlete rated against your roster openings' },
                  { text: 'Filter by class, position, state and measurements' },
                  { text: 'Build lists and export them to CSV' },
                  { text: 'Contact windows tracked for your division' },
                ]
              : [
                  { text: 'Smart college matching algorithm' },
                  { text: 'Direct coach communication' },
                  { text: 'Track recruiting progress' },
                  { text: 'Share highlights and stats' }
                ]
            ).map((feature, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)',
                borderLeft: '4px solid #f9550d'
              }}>
                <span style={{ fontSize: '16px', fontWeight: '500' }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="login-section" style={{
          flex: 1,
          maxWidth: '500px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div className="login-card" style={{
            width: '100%',
            background: 'white',
            borderRadius: '24px',
            padding: '52px 48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>

          {/* Family or coach. Hidden while accepting a family invitation. */}
          {!invitationToken && <AudienceSwitch coach={coachEntry} />}

          {/* Invitation Banner */}
          {invitationToken && invitationInfo && (
            <div style={{
              background: '#E3F2FD',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center',
              border: '1px solid #1976D2'
            }}>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1565C0',
                margin: 0,
                fontFamily: 'Manrope, sans-serif'
              }}>
                You've been invited to join the <strong>{invitationInfo.family_name}</strong>
              </p>
              <p style={{
                fontSize: '14px',
                color: '#1976D2',
                margin: '8px 0 0 0',
                fontFamily: 'Manrope, sans-serif'
              }}>
                Sign in or create an account to accept this invitation
              </p>
            </div>
          )}

          {/* Referral Banner */}
          <ReferralBanner variant="compact" />
          
          <h2 style={{
            fontSize: '34px',
            fontWeight: '700',
            color: '#151133',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            {coachEntry ? 'Coach sign in' : 'Welcome Back'}
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#64748b',
            marginBottom: '36px',
            textAlign: 'center'
          }}>
            {coachEntry
              ? 'Sign in to the College Coach Portal.'
              : 'Sign in to continue the recruiting journey'}
          </p>

          {/* Email Verification Required Banner */}
          {isEmailNotVerified && (
            <div style={{
              padding: '24px',
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #fef3cd 0%, #fff8e7 100%)',
              border: '2px solid #f9550d',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)'
              }}>
                <Mail size={28} color="white" />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#151133',
                marginBottom: '8px'
              }}>
                Check Your Email
              </h3>
              {unverifiedEmail && (
                <p style={{
                  fontSize: '15px',
                  color: '#f9550d',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  {unverifiedEmail}
                </p>
              )}
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                marginBottom: '16px',
                lineHeight: '1.6'
              }}>
                We sent a verification link to your email. Please click the link to verify your account before signing in.
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}>
                  Didn't receive the email? Check your spam folder.
                </p>
                <button
                  onClick={() => handleResendVerification()}
                  disabled={resendingEmail || resendSuccess}
                  style={{
                    padding: '12px 28px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: resendSuccess ? '#10b981' : '#f9550d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: resendingEmail || resendSuccess ? 'not-allowed' : 'pointer',
                    opacity: resendingEmail ? 0.7 : 1,
                    fontFamily: 'Manrope, sans-serif',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  {resendingEmail ? (
                    'Sending...'
                  ) : resendSuccess ? (
                    <>
                      <Check size={16} aria-label="Sent" />
                      Verification Email Sent!
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
                {resendSuccess && (
                  <p style={{
                    fontSize: '12px',
                    color: '#10b981',
                    fontWeight: '500',
                    marginTop: '4px'
                  }}>
                    Check your inbox and spam folder
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Wrong-provider banner: email already registered with a different method */}
          {isWrongProvider && (
            <div style={{
              padding: '24px',
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #fef3cd 0%, #fff8e7 100%)',
              border: '2px solid #f9550d',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#151133',
                margin: '0 0 8px 0'
              }}>
                Account already exists
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.6',
                margin: '0 0 16px 0'
              }}>
                {wrongProviderMessage || `This email is already registered using ${wrongProviderLabel}. Please sign in with ${wrongProviderLabel} instead.`}
              </p>
              <button
                onClick={handleSignInWithCorrectMethod}
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#f9550d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                Sign in with {wrongProviderLabel}
              </button>
            </div>
          )}

          {/* General Error Banner (non-email-verification, non-wrong-provider errors) */}
          {error && !isEmailNotVerified && !isWrongProvider && (
            <div style={{
              padding: '16px',
              marginBottom: '24px',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '12px',
              color: '#c00',
              fontSize: '14px'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Collapsed-email sign-in chooser — three "Continue with X"
              buttons up top. Clicking "Continue with Email" expands the
              inline email/password form. Replaces the old "Sign In"
              button that bounced to Auth0 Universal Login. */}
          {!showEmailForm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Buttons mirror SignUpPage for visual consistency — same
                  padding, borders, 24x24 SVGs, color treatment. */}
              <button
                type="button"
                onClick={() => handleSocial('google-oauth2')}
                style={{
                  width: '100%', padding: '16px', background: 'white',
                  border: '2px solid #e2e8f0', borderRadius: '12px',
                  fontSize: '15px', fontWeight: '600', color: '#1f2937',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '12px',
                  transition: 'all 0.2s ease', fontFamily: 'Manrope, sans-serif',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <button
                type="button"
                onClick={() => handleSocial('apple')}
                style={{
                  width: '100%', padding: '16px', background: 'black',
                  border: '2px solid black', borderRadius: '12px',
                  fontSize: '15px', fontWeight: '600', color: 'white',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '12px',
                  transition: 'all 0.2s ease', fontFamily: 'Manrope, sans-serif',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1f2937';
                  e.currentTarget.style.borderColor = '#1f2937';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'black';
                  e.currentTarget.style.borderColor = 'black';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </button>

              <button
                type="button"
                onClick={() => setShowEmailForm(true)}
                style={{
                  width: '100%', padding: '16px',
                  background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                  border: 'none', borderRadius: '12px',
                  fontSize: '15px', fontWeight: '700', color: 'white',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '12px',
                  fontFamily: 'Manrope, sans-serif',
                  boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 85, 13, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
                }}
              >
                <Mail size={18} />
                Continue with Email
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Both keep the fixed 48px height and 16px gutter the sign-in
                  card was laid out around; everything else — resting border,
                  focus ring, invalid, disabled — now comes from .aps-input.
                  The labels are the placeholders, so each carries aria-label. */}
              <TextInput
                type="email"
                aria-label="Email address"
                value={formEmail}
                onChange={(e) => { setFormEmail(e.target.value); if (formProvider) setFormProvider(null); }}
                onBlur={handleEmailBlur}
                placeholder="Email address"
                autoFocus
                style={{ height: 48, padding: '0 16px' }}
              />
              <TextInput
                type="password"
                aria-label="Password"
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                placeholder="Password"
                style={{ height: 48, padding: '0 16px' }}
              />
              {/* Provider-aware reset password — hidden when the backend
                  reports the user signed up via Google/Apple (no password
                  to reset). Sends the reset email directly via
                  /api/auth/forgot-password; no Universal Login bounce. */}
              {!formProvider && !resetSent && (
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetSubmitting}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: '#f9550d', fontSize: 13, fontWeight: 600,
                    cursor: resetSubmitting ? 'not-allowed' : 'pointer',
                    opacity: resetSubmitting ? 0.6 : 1,
                    textAlign: 'left',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {resetSubmitting ? 'Sending…' : 'Reset password'}
                </button>
              )}
              {resetSent && !formProvider && (
                <div style={{
                  padding: 12, background: '#D1FAE5', border: '1px solid #6EE7B7',
                  borderRadius: 10, fontSize: 13, color: '#065F46',
                  fontFamily: 'Manrope, sans-serif', lineHeight: 1.5,
                }}>
                  If an account exists for that email, a reset link is on its way. Check spam if you don't see it in a couple of minutes.
                </div>
              )}
              {/* Provider-mismatch inline error: user signed up with a
                  social provider, then typed email/password here. Show
                  a Continue-with-{provider} button instead of a dead
                  reset-password path. */}
              {formProvider && (
                <div style={{
                  padding: 14, background: '#FEF3C7', border: '1px solid #FCD34D',
                  borderRadius: 10, fontFamily: 'Manrope, sans-serif',
                }}>
                  <p style={{ margin: 0, marginBottom: 10, fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
                    This email is registered with <strong>{formProvider.label}</strong>. Sign in with {formProvider.label} instead.
                  </p>
                  <button
                    type="button"
                    onClick={handleSocialSwitch}
                    style={{
                      padding: '8px 14px', fontSize: 13, fontWeight: 700,
                      background: '#F9550D', color: 'white', border: 'none',
                      borderRadius: 8, cursor: 'pointer',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    Continue with {formProvider.label}
                  </button>
                </div>
              )}
              {formError && !formProvider && (
                <div style={{
                  padding: 12, background: '#FEE', border: '1px solid #FCC',
                  borderRadius: 10, color: '#C00', fontSize: 13,
                  fontFamily: 'Manrope, sans-serif',
                }}>
                  {formError}
                  {/* EMAIL_NOT_VERIFIED has no self-serve recovery inline —
                      give the user a way to re-trigger the verification email
                      right here instead of stranding them on "verify first". */}
                  {formEmailUnverified && (
                    <button
                      type="button"
                      onClick={() => handleResendVerification(formEmail.trim().toLowerCase())}
                      disabled={resendingEmail}
                      style={{
                        display: 'block', marginTop: 8, background: 'none',
                        border: 'none', padding: 0, color: '#f9550d', fontSize: 13,
                        fontWeight: 700, textAlign: 'left',
                        cursor: resendingEmail ? 'not-allowed' : 'pointer',
                        opacity: resendingEmail ? 0.6 : 1,
                        fontFamily: 'Manrope, sans-serif', textDecoration: 'underline',
                      }}
                    >
                      {resendingEmail ? 'Sending…' : 'Resend verification email'}
                    </button>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={formSubmitting}
                style={{
                  width: '100%', padding: '14px 16px', fontSize: 15, fontWeight: 700,
                  background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                  color: 'white', border: 'none', borderRadius: 10,
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                  opacity: formSubmitting ? 0.6 : 1,
                  fontFamily: 'Manrope, sans-serif',
                  boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                }}
              >
                {formSubmitting ? 'Signing in...' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEmailForm(false);
                  setFormError(null);
                  setFormProvider(null);
                  setFormPassword('');
                }}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#6B7280', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', textAlign: 'center', marginTop: 4,
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                <ArrowLeft size={13} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />Back to sign-in options
              </button>
            </form>
          )}

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '24px 0',
            gap: '16px'
          }}>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
            <span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '500' }}>OR</span>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          </div>

          {/* Sign Up Link */}
          <div style={footerLine}>
            Don't have an account?{' '}
            <a href={authPath('/signup', coachEntry)} style={footerLink} {...footerLinkHover}>
              Create Account
            </a>
          </div>

        </div>
      </div>
      </div>

      {/* Mobile responsive adjustments */}
      <style>
        {`
          @media (max-width: 1200px) {
            .two-column-container {
              gap: 40px !important;
            }
            .login-card {
              padding: 40px 36px !important;
            }
          }

          @media (max-width: 768px) {
            /* Stack vertically on mobile, sign in first */
            .two-column-container {
              flex-direction: column !important;
              gap: 40px !important;
              align-items: center !important;
            }
            .login-section {
              order: 1 !important;
              max-width: 100% !important;
              width: 100% !important;
            }
            .branding-section {
              order: 2 !important;
              max-width: 100% !important;
              width: 100% !important;
              text-align: center !important;
            }
            .branding-section h2 {
              text-align: center !important;
            }
            .login-card {
              padding: 32px 24px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

// Footer lines under the card. Shared so every line and link matches.
const footerLine: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '14px',
  color: '#64748b',
};
const footerLink: React.CSSProperties = {
  color: '#f9550d',
  textDecoration: 'none',
  fontWeight: '600',
  transition: 'color 0.2s ease',
};
const footerLinkHover = {
  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = '#ff6b2c'; },
  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = '#f9550d'; },
};

export default LoginPage;
